import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { SignOut, DeviceMobile, AppleLogo, AndroidLogo, Broadcast, Info, CaretDown, DownloadSimple, ChatCircleText, ShieldCheck, Eye, UserCircle, ChartBar, Warning, Sparkle, ClockCounterClockwise, DotsThreeVertical, Export, LockKey, Lifebuoy, CaretRight, Receipt } from "@phosphor-icons/react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Switch } from "@/components/ui/switch";
import { Shell } from "@/components/Shell";
import { ShareGuide } from "@/components/ShareGuide";
import { NfcWriteDialog } from "@/components/NfcWriteDialog";
import { useApp } from "@/context/AppContext";
import { APP_VERSION, CHANGELOG } from "@/changelog";
import api from "@/lib/api";

const THEME_SWATCHES = {
  lavender: "#A855F7", mint: "#10B981", peach: "#F97316", sky: "#0EA5E9", pink: "#EC4899",
  petrol: "#0E7C86", navy: "#2F4F9E", graphit: "#6B7280", forest: "#1F7A45", bordeaux: "#8A2244",
  teal: "#14b8a6", blue: "#3b82f6", purple: "#a855f7", orange: "#f97316", green: "#22c55e",
};
const ACCENT_LABELS = { teal: "Teal", blue: "Blau", purple: "Lila", orange: "Orange", green: "Grün" };
const THEME_LABELS = {
  lavender: "Lavendel", mint: "Mintgrün", peach: "Pfirsich", sky: "Himmelblau", pink: "Rosa",
};

export default function Settings() {
  const { t, lang, setLang, mode, setMode, theme, setTheme, THEMES, darkMode, setDarkMode, logout, user, supportEnabled } = useApp();
  const [redeemCode, setRedeemCode] = useState("");
  const redeemNow = async () => {
    try { const r = await api.post("/activation/redeem", { code: redeemCode }); toast.success(r.data.message || "Code eingelöst"); setRedeemCode(""); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };
  const navigate = useNavigate();
  const [nfcOpen, setNfcOpen] = useState(false);
  const [openVer, setOpenVer] = useState({});
  const [fb, setFb] = useState("");
  const [fbAnon, setFbAnon] = useState(false);
  const [fbSending, setFbSending] = useState(false);
  const nfcSupported = typeof window !== "undefined" && "NDEFReader" in window;

  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [rptPw, setRptPw] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const maskEmail = (e) => { if (!e || !e.includes("@")) return e || ""; const [a, b] = e.split("@"); return (a[0] || "") + "***@" + b; };
  const requestEmail = async () => {
    if (!newEmail.trim()) return;
    setEmailBusy(true);
    try { await api.post("/auth/request-email-change", { new_email: newEmail }); toast.success(t("emailChangeSent")); setNewEmail(""); setEmailOpen(false); }
    catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
    finally { setEmailBusy(false); }
  };
  const changePw = async () => {
    if (newPw !== rptPw) { toast.error(t("pwMismatch")); return; }
    if (newPw.length < 8) { toast.error(t("pwTooShort")); return; }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: curPw, new_password: newPw });
      toast.success(t("pwChanged"));
      setCurPw(""); setNewPw(""); setRptPw("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
    finally { setPwBusy(false); }
  };

  const sendFeedback = async () => {
    if (!fb.trim()) return;
    setFbSending(true);
    try {
      await api.post("/feedback", { message: fb, anonymous: fbAnon });
      setFb("");
      toast.success(t("feedbackSent"));
    } catch { toast.error("Fehler"); }
    finally { setFbSending(false); }
  };

  useEffect(() => {}, []);

  const writeNfc = () => setNfcOpen(true);

  return (
    <Shell back="/dashboard">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
        <h1 className="text-3xl font-heading font-semibold text-gray-900">{t("settings")}</h1>

        <div className="sb-card p-5" data-testid="redeem-code-card">
          <p className="font-heading font-semibold text-gray-900">Aktivierungscode einlösen</p>
          <p className="font-body text-xs text-gray-500 mt-0.5">Löse deinen gekauften Code ein – dein nächster SafeBuddy wird freigeschaltet.</p>
          <div className="flex gap-2 mt-2">
            <input data-testid="redeem-code-input" className="sb-input" placeholder="SBXXXX-XXXX" value={redeemCode} onChange={(e) => setRedeemCode(e.target.value)} />
            <button data-testid="redeem-code-btn" onClick={redeemNow} disabled={!redeemCode.trim()} className="sb-btn px-4 bg-white font-medium shrink-0 disabled:opacity-50">Einlösen</button>
          </div>
        </div>

        <button data-testid="settings-orders" onClick={() => navigate("/orders")} className="sb-card p-5 w-full flex items-center justify-between active:scale-[0.99] transition-transform">
          <div className="flex items-center gap-3">
            <Receipt size={24} weight="duotone" style={{ color: "var(--app-strong)" }} />
            <div className="text-left">
              <p className="font-heading font-semibold text-gray-900">{t("ordersCardTitle")}</p>
              <p className="font-body text-xs text-gray-500">{t("ordersCardSubtitle")}</p>
            </div>
          </div>
          <CaretRight size={18} className="text-gray-400" />
        </button>

        {supportEnabled && (
          <button data-testid="settings-support" onClick={() => navigate("/support")} className="sb-card p-5 w-full flex items-center justify-between active:scale-[0.99] transition-transform">            <div className="flex items-center gap-3">
              <Lifebuoy size={24} weight="duotone" style={{ color: "var(--app-strong)" }} />
              <div className="text-left">
                <p className="font-heading font-semibold text-gray-900">Hilfe &amp; Support</p>
                <p className="font-body text-xs text-gray-500">FAQ, Problemlösung &amp; Ticket erstellen</p>
              </div>
            </div>
            <CaretRight size={18} className="text-gray-400" />
          </button>
        )}

        <div className="sb-card p-5">
          <h2 className="text-lg font-heading font-semibold mb-1">{t("colorTheme")}</h2>
          <p className="font-body text-xs text-gray-500 mb-4">Wähle deine Akzentfarbe für die ganze App.</p>
          <div className="flex flex-wrap gap-3" data-testid="theme-swatches">
            {THEMES.map((th) => (
              <button key={th} data-testid={`theme-${th}`} onClick={() => setTheme(th)}
                className="flex flex-col items-center gap-1.5 active:scale-90 transition-transform">
                <span className="w-12 h-12 rounded-full border-2" style={{ background: THEME_SWATCHES[th], borderColor: theme === th ? "#1F2937" : "transparent", boxShadow: theme === th ? "0 0 0 3px var(--app-strong)" : "none" }} />
                <span className="text-[11px] font-body text-gray-600">{ACCENT_LABELS[th] || th}</span>
              </button>
            ))}
          </div>
          <div className="mt-5 pt-4 border-t border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-body font-medium text-gray-900">Dunkelmodus</span>
              <span className="text-lg" aria-hidden>{darkMode ? "🌙" : "☀️"}</span>
            </div>
            <Switch data-testid="dark-mode-toggle" checked={darkMode} onCheckedChange={setDarkMode} />
          </div>
        </div>

        <div className="sb-card p-5">
          <h2 className="text-lg font-heading font-semibold mb-4">{t("language")}</h2>
          <div className="flex gap-3">
            <button data-testid="lang-de" onClick={() => setLang("de")} className={`flex-1 py-3 rounded-xl border-2 border-gray-800 font-heading font-medium ${lang === "de" ? "text-white" : "bg-white text-gray-700"}`} style={lang === "de" ? { background: "var(--app-primary)" } : {}}>{t("german")}</button>
            <button data-testid="lang-en" onClick={() => setLang("en")} className={`flex-1 py-3 rounded-xl border-2 border-gray-800 font-heading font-medium ${lang === "en" ? "text-white" : "bg-white text-gray-700"}`} style={lang === "en" ? { background: "var(--app-primary)" } : {}}>{t("english")}</button>
          </div>
        </div>

        <div className="sb-card p-5">
          <h2 className="text-lg font-heading font-semibold mb-3 flex items-center gap-2"><Broadcast size={24} weight="duotone" />{t("helpTitle")}</h2>
          <Accordion type="single" collapsible defaultValue="how">
            <AccordionItem value="how">
              <AccordionTrigger data-testid="nfc-howto" className="font-heading">{t("nfcHowto")}</AccordionTrigger>
              <AccordionContent className="font-body text-gray-600 leading-relaxed">{t("nfcExplain")}</AccordionContent>
            </AccordionItem>
            <AccordionItem value="guide">
              <AccordionTrigger data-testid="nfc-guide" className="font-heading">{t("nfcGuide")}</AccordionTrigger>
              <AccordionContent>
                <button data-testid="open-guide-btn" onClick={() => navigate("/guide")}
                  className="sb-btn w-full py-3 text-white font-medium flex items-center justify-center gap-2" style={{ background: "var(--app-primary)" }}>
                  <ChartBar size={18} weight="bold" />{t("openGuide")}
                </button>
                <a data-testid="nfc-guide-pdf" href="/nfc-guide.pdf" download="SafeBuddy-NFC-Anleitung.pdf"
                  className="sb-btn w-full py-3 mt-3 bg-white font-medium flex items-center justify-center gap-2"><DownloadSimple size={18} weight="bold" />{t("downloadPdf")}</a>
                <ShareGuide />
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>

        <div className="sb-card p-5">
          <h2 className="text-lg font-heading font-semibold mb-2 flex items-center gap-2"><ChatCircleText size={22} weight="duotone" />{t("feedbackTitle")}</h2>
          <p className="text-sm font-body text-gray-500 mb-3">{t("feedbackHint")}</p>
          <textarea data-testid="feedback-input" className="sb-input" rows={3} placeholder={t("feedbackPlaceholder")} value={fb} onChange={(e) => setFb(e.target.value)} />
          <div className="flex items-center justify-between mt-3">
            <span className="font-body text-sm">{t("anonymous")}</span>
            <Switch data-testid="feedback-anon" checked={fbAnon} onCheckedChange={setFbAnon} />
          </div>
          <button data-testid="feedback-send" onClick={sendFeedback} disabled={fbSending || !fb.trim()}
            className="sb-btn w-full py-3.5 mt-3 text-white font-medium disabled:opacity-60" style={{ background: "var(--app-primary)" }}>
            {fbSending ? "…" : t("sendFeedback")}
          </button>
        </div>

        <div className="sb-card p-5">
          <h2 className="text-lg font-heading font-semibold mb-3 flex items-center gap-2"><ShieldCheck size={22} weight="duotone" />{t("legalTitle")}</h2>
          <Accordion type="single" collapsible>
            <AccordionItem value="imprint">
              <AccordionTrigger data-testid="legal-imprint" className="font-heading">{t("imprint")}</AccordionTrigger>
              <AccordionContent className="font-body text-gray-600 whitespace-pre-line leading-relaxed">{t("imprintText")}</AccordionContent>
            </AccordionItem>
            <AccordionItem value="privacy">
              <AccordionTrigger data-testid="legal-privacy" className="font-heading">{t("privacy")}</AccordionTrigger>
              <AccordionContent className="font-body text-gray-600 whitespace-pre-line leading-relaxed">{t("privacyText")}</AccordionContent>
            </AccordionItem>
            <AccordionItem value="policies">
              <AccordionTrigger data-testid="legal-policies" className="font-heading">{t("policies")}</AccordionTrigger>
              <AccordionContent className="font-body text-gray-600 whitespace-pre-line leading-relaxed">{t("policiesText")}</AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>

        <div className="sb-card p-5" data-testid="password-card">
          <h2 className="text-lg font-heading font-semibold mb-3 flex items-center gap-2"><LockKey size={22} weight="duotone" style={{ color: "var(--app-strong)" }} />{t("changePassword")}</h2>
          <div className="mb-4 pb-4 border-b border-gray-200">
            <p className="text-xs font-body text-gray-500 uppercase tracking-wider">{t("currentEmailLabel")}</p>
            <p data-testid="settings-current-email" className="font-body text-gray-800 mt-0.5">{maskEmail(user?.email)}</p>
            {!emailOpen ? (
              <button data-testid="change-email-btn" onClick={() => setEmailOpen(true)} className="sb-btn px-3 py-1.5 bg-white text-sm mt-2">{t("changeEmailTitle")}</button>
            ) : (
              <div className="mt-2 space-y-2">
                <input data-testid="new-email-input" type="email" className="sb-input" placeholder={t("newEmailLabel")} value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
                <button data-testid="request-email-btn" onClick={requestEmail} disabled={emailBusy || !newEmail.trim()} className="sb-btn w-full py-2.5 text-white font-medium disabled:opacity-50" style={{ background: "var(--app-primary)" }}>{t("requestEmailChange")}</button>
              </div>
            )}
          </div>
          <div className="space-y-3">
            <input data-testid="pw-current" type="password" className="sb-input" placeholder={t("currentPassword")} value={curPw} onChange={(e) => setCurPw(e.target.value)} />
            <input data-testid="pw-new" type="password" className="sb-input" placeholder={t("newPassword")} value={newPw} onChange={(e) => setNewPw(e.target.value)} />
            <input data-testid="pw-repeat" type="password" className="sb-input" placeholder={t("repeatPassword")} value={rptPw} onChange={(e) => setRptPw(e.target.value)} />
            <button data-testid="pw-save" onClick={changePw} disabled={pwBusy || !curPw || !newPw || !rptPw}
              className="sb-btn w-full py-3 text-white font-medium disabled:opacity-50" style={{ background: "var(--app-primary)" }}>{pwBusy ? "…" : t("savePassword")}</button>
          </div>
        </div>

        <div className="sb-card p-5" data-testid="pwa-card">
          <h2 className="text-lg font-heading font-semibold mb-2 flex items-center gap-2"><DownloadSimple size={22} weight="duotone" />{t("pwaTitle")}</h2>
          <p className="text-sm font-body text-gray-600 mb-3">{t("pwaHint")}</p>
          <div className="space-y-3">
            <div data-testid="home-android" className="rounded-2xl border-2 border-gray-800 bg-white p-4">
              <div className="flex items-center gap-2 mb-3">
                <AndroidLogo size={22} weight="fill" style={{ color: "#3DDC84" }} />
                <span className="font-heading font-semibold text-gray-900">{t("homeAndroidTitle")}</span>
              </div>
              <ol className="space-y-2.5 text-sm font-body text-gray-700">
                <li className="flex items-start gap-3"><span className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-heading font-semibold" style={{ background: "var(--app-primary)" }}>1</span><span>{t("homeAndroidStep1")}</span></li>
                <li className="flex items-start gap-3"><span className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-heading font-semibold" style={{ background: "var(--app-primary)" }}>2</span><span className="flex items-center gap-1 flex-wrap">{t("homeAndroidStep2")}<DotsThreeVertical size={18} weight="bold" className="inline" /></span></li>
                <li className="flex items-start gap-3"><span className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-heading font-semibold" style={{ background: "var(--app-primary)" }}>3</span><span>{t("homeAndroidStep3")}</span></li>
              </ol>
            </div>
            <div data-testid="home-ios" className="rounded-2xl border-2 border-gray-800 bg-white p-4">
              <div className="flex items-center gap-2 mb-3">
                <AppleLogo size={22} weight="fill" className="text-gray-900" />
                <span className="font-heading font-semibold text-gray-900">{t("homeIosTitle")}</span>
              </div>
              <ol className="space-y-2.5 text-sm font-body text-gray-700">
                <li className="flex items-start gap-3"><span className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-heading font-semibold" style={{ background: "var(--app-primary)" }}>1</span><span>{t("homeIosStep1")}</span></li>
                <li className="flex items-start gap-3"><span className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-heading font-semibold" style={{ background: "var(--app-primary)" }}>2</span><span className="flex items-center gap-1 flex-wrap">{t("homeIosStep2")}<Export size={18} weight="bold" className="inline" /></span></li>
                <li className="flex items-start gap-3"><span className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-heading font-semibold" style={{ background: "var(--app-primary)" }}>3</span><span>{t("homeIosStep3")}</span></li>
              </ol>
            </div>
          </div>
        </div>

        <div className="sb-card p-5" data-testid="version-history">
          <h2 className="text-lg font-heading font-semibold mb-1 flex items-center gap-2"><ClockCounterClockwise size={22} weight="duotone" />{t("versionHistory")}</h2>
          <p className="text-xs font-body text-gray-500 mb-3">{t("version")} {APP_VERSION}</p>
          <div className="space-y-3">
            {CHANGELOG.map((v, i) => {
              const expanded = i === 0 || !!openVer[v.version];
              const toggle = () => { if (i === 0) return; setOpenVer((o) => ({ ...o, [v.version]: !o[v.version] })); };
              return (
                <div key={v.version} data-testid={`version-${v.version}`} className="rounded-2xl border-2 border-gray-800 p-3"
                  style={i === 0 ? { background: "color-mix(in srgb, var(--app-primary) 12%, #fff)" } : {}}>
                  <button type="button" data-testid={`version-toggle-${v.version}`} onClick={toggle}
                    disabled={i === 0} className="w-full flex items-center justify-between text-left" style={i === 0 ? { cursor: "default" } : {}}>
                    <span className="font-heading font-semibold text-gray-900 flex items-center gap-2">v{v.version}
                      {i === 0 && <span className="text-[10px] uppercase px-2 py-0.5 rounded-full text-white align-middle" style={{ background: "var(--app-primary)" }}>{t("currentVersion")}</span>}
                    </span>
                    <span className="flex items-center gap-2">
                      <span className="text-xs font-body text-gray-500">{v.date}</span>
                      {i !== 0 && <CaretDown size={16} weight="bold" className="text-gray-400 transition-transform" style={{ transform: expanded ? "rotate(180deg)" : "none" }} />}
                    </span>
                  </button>
                  {expanded && (
                    <ul className="list-disc pl-5 mt-1.5 text-sm font-body text-gray-700 space-y-0.5">
                      {v.changes.map((c, j) => (<li key={j}>{c}</li>))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <button data-testid="restart-onboarding" onClick={() => { localStorage.removeItem("sb_onboarded"); navigate("/dashboard"); }}
          className="sb-btn w-full py-4 bg-white font-medium flex items-center justify-center gap-2">
          <Info size={20} weight="bold" />{t("restartOnboarding")}
        </button>

        <button data-testid="logout-btn" onClick={logout} className="sb-btn w-full py-4 bg-white font-medium flex items-center justify-center gap-2 text-red-500">
          <SignOut size={20} weight="bold" />{t("logout")}
        </button>
      </motion.div>
      <NfcWriteDialog open={nfcOpen} onOpenChange={setNfcOpen} />
    </Shell>
  );
}
